import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8002";

function getAuthSession() {
  try {
    const raw = localStorage.getItem("orbismed_patient_auth");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getAccessToken() {
  return getAuthSession()?.access_token || null;
}

export const patientSettingsHttp = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

patientSettingsHttp.interceptors.request.use((config) => {
  const token = getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

function unwrapError(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
  }

  return detail || err?.message || fallback;
}

export const patientSettingsApi = {
  async me() {
    try {
      const res = await patientSettingsHttp.get("/api/v1/patients/me/settings");
      return res.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to load settings."));
    }
  },

  async updateProfile(payload) {
    try {
      const res = await patientSettingsHttp.patch(
        "/api/v1/patients/me/settings",
        payload
      );
      return res.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to update profile."));
    }
  },

  /**
   * Record the date of birth on its own, for the prompt shown while booking an
   * age-restricted service. The value is a plain "YYYY-MM-DD" calendar date.
   */
  async setDateOfBirth(dateOfBirth) {
    try {
      const res = await patientSettingsHttp.put(
        "/api/v1/patients/me/settings/date-of-birth",
        { date_of_birth: dateOfBirth }
      );
      return res.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to save your date of birth."));
    }
  },

  async changePassword(payload) {
    try {
      const res = await patientSettingsHttp.post(
        "/api/v1/patients/me/settings/change-password",
        payload
      );
      return res.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to change password."));
    }
  },

  async uploadProfilePhoto(file) {
    try {
      const uploadUrlRes = await patientSettingsHttp.post(
        "/api/v1/patients/me/settings/profile-photo/upload-url",
        {
          file_name: file.name,
          content_type: file.type,
          file_size_bytes: file.size,
        }
      );

      const uploadData = uploadUrlRes.data;

      await axios.put(uploadData.upload_url, file, {
        headers: {
          "Content-Type": uploadData.content_type || file.type,
        },
      });

      const confirmRes = await patientSettingsHttp.post(
        "/api/v1/patients/me/settings/profile-photo/confirm",
        {
          key: uploadData.key,
        }
      );

      return confirmRes.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to upload profile photo."));
    }
  },

  async deleteProfilePhoto() {
    try {
      const res = await patientSettingsHttp.delete(
        "/api/v1/patients/me/settings/profile-photo"
      );

      return res.data;
    } catch (err) {
      throw new Error(unwrapError(err, "Failed to remove profile photo."));
    }
  },
};